import React, { useEffect, useState } from "react";
import axios from "axios";
import { buildAxiosAuthenticationHeadersForMultipart } from "@src/cloud-connect/api/util/build-axios-authentication-headers";

interface AuthImageProps {
  /** Image URL. relative paths are resolved against baseUrl. Null while an
   * attachment upload is still pending; the component renders nothing. */
  src: string | null | undefined;
  /** Base URL used to resolve relative src paths. */
  baseUrl?: string;
  alt: string;
  className?: string;
  title?: string;
}

/**
 * Renders an image fetched with authentication headers as a blob URL — for
 * backend endpoints that stream protected images and cannot be used as plain
 * <img> sources.
 */
export const AuthImage: React.FC<AuthImageProps> = ({
  src,
  baseUrl,
  alt,
  className,
  title,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const fetchImage = async () => {
      if (!src) return;

      const resolvedSrc =
        src.startsWith("/") && baseUrl ? `${baseUrl}${src}` : src;

      try {
        const response = await axios.get<Blob>(resolvedSrc, {
          headers: buildAxiosAuthenticationHeadersForMultipart(),
          responseType: "blob",
        });

        if (cancelled) return;

        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      } catch {
        // Leave the image empty — a failed thumbnail must not be noisy.
      }
    };

    void fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, baseUrl]);

  if (!blobUrl) {
    return null;
  }

  return <img src={blobUrl} alt={alt} className={className} title={title} />;
};
